import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import {
  countryRequest,
  forexRequest,
} from "../common/customServiceCall/serviceCall";
import { InputText } from "primereact/inputtext";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import { format } from "date-fns";
import CountryFlag from "react-country-flag";
import CustomDialog from "../common/tableComponent.js/dialog";
import axios from "axios";
import qs from "query-string";
import { Dropdown } from "primereact/dropdown";

const Forex = () => {
  const userDetails = useSelector((state) => state.login.userDetails);
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([] || null);
  const [tradingCurrency, setTradingCurrency] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [countries, setCountries] = useState([]);

  const [formData, setFormData] = useState({
    institutionName: "",
    institutionId: "",
    countryCode: "",
    tradingCurrency: "",
    customRate: 0,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleInstitutionChange = (e) => {
    const selectedInstitution = institutions.find(
      (inst) => inst.name === e.value
    );
    setFormData((prev) => ({
      ...prev,
      institutionName: e.value,
      institutionId: selectedInstitution ? selectedInstitution.user_id : "",
    }));
  };

  const fetchData = async () => {
    setLoading(true);

    const requestData = {
      tradingCurrency: tradingCurrency || "",
      institutionId: institutionId || "",
      countryCode: countryCode || "",
    };

    const filteredRequestData = Object.fromEntries(
      Object.entries(requestData).filter(([key, value]) => value !== "")
    );

    const queryString = new URLSearchParams(filteredRequestData).toString();

    try {
      const response = await forexRequest(
        "get",
        `api/v1/fxrates?${queryString}`
      );

      if (response.code === "00") {
        setRates(response.data || []); // Set data or empty array
      } else {
        setRates([]); // No data, clear the table
        console.error("Failed to fetch data:", response.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tradingCurrency, countryCode, institutionId]); // Re-fetch data when filters change

  const getSearchedData = () => {
    fetchData(); // Re-fetch the data with the updated filters
  };

  const countryBodyTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <CountryFlag
          countryCode={rowData.countryCode}
          svg
          style={{ width: "25px", height: "15px", marginRight: "8px" }}
        />
        {rowData.countryCode}
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return <p>{format(new Date(rowData.createdAt), "yyyy-MM-dd HH:mm:ss")}</p>;
  };

  const floatLabel = {
    fontWeight: "500",
    color: "rgb(153, 153, 153)",
    fontSize: "12px",
  };

  const labelStyle = {
    paddingLeft: "10px",
    fontSize: "12px",
    color: "rgb(92, 74, 181)",
    fontWeight: "500",
  };

  const handleAddForex = async () => {
    await getInstitutionList();
    await fetchCountries();
    setFormData({
      institutionName: "",
      institutionId: "",
      countryCode: "",
      tradingCurrency: "",
      customRate: 0,
    });
    setShowAddModal(true);
  };

  const getInstitutionList = async () => {
    const config = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    const data = {
      token: userDetails._id,
      page: 1,
      size: 1000,
    };
    try {
      setLoading(true);
      const response = await axios.post(
        global.config.main.baseUrl + "list-inst",
        qs.stringify(data),
        config
      );

      if (response.status === 200) {
        setInstitutions(response.data.data); // Store the institution data
        return response.data.data;
      } else {
        console.log("Failed to fetch data:", response.msg);
        return [];
      }
    } catch (error) {
      console.log("Error fetching data:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    setLoading(true);

    try {
      const response = await countryRequest("get", `api/v1/countries/list-all`);

      if (response.code === "00") {
        setCountries(response.data || []); // Set data or empty array
      } else {
        setCountries([]); // No data, clear the table
        console.error("Failed to fetch data:", response.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForex = async () => {
    if (!formData.institutionName || !formData.institutionId) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a valid institution",
        life: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const response = await forexRequest(
        "post",
        "api/v1/fxrates",
        JSON.stringify(formData)
      );

      if (response.code === "00") {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Forex rate added successfully",
          life: 3000,
        });
        setShowAddModal(false);
        fetchData(); // Refresh the table data
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.msg || "Failed to add forex rate",
          life: 3000,
        });
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while adding forex rate",
        life: 3000,
      });
      console.log("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {" "}
      <section
        className="p-my-4 p-md-12 p-xl-12"
        style={{ padding: "0px 10%" }}
      >
        <Toast ref={toast} />

        <div className="p-grid p-fluid p-mb-4">
          <div
            className="p-col-12 p-sm-12 p-md-10 p-lg-10 p-xl-10 p-md-offset-1 p-lg-offset-1 p-xl-offset-1"
            style={{ textAlign: "center" }}
          >
            <span className="p-tag custom-heading">Forex</span>
          </div>
        </div>

        <div className="card" style={{ paddingTop: "0px" }}>
          <div
            className="p-grid p-align-center "
            style={{ paddingTop: "10px" }}
          >
            {/* Search Input */}
            <div className=" p-col-12 p-md-3">
              <label style={floatLabel}>Trading Currency</label>
              <div className="p-inputgroup">
                <InputText
                  value={tradingCurrency}
                  onChange={(e) => setTradingCurrency(e.target.value)}
                  placeholder="Enter trading Currency"
                />
              </div>
            </div>
            <div className=" p-col-12 p-md-3">
              <label style={floatLabel}>Institution Id</label>
              <div className="p-inputgroup">
                <InputText
                  value={institutionId}
                  onChange={(e) => setInstitutionId(e.target.value)}
                  placeholder="Enter institution Id"
                />
              </div>
            </div>
            <div className=" p-col-12 p-md-3">
              <label style={floatLabel}>Country Code</label>
              <div className="p-inputgroup">
                <InputText
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  placeholder="Enter country code"
                />
              </div>
            </div>
            <div className=" p-col-12 p-md-1">
              <Button
                onClick={getSearchedData}
                icon="pi pi-search"
                className="p-button-info"
                style={{ marginTop: "20px" }}
              />
            </div>
            <div className=" p-col-12 p-md-1">
              <Button
                onClick={handleAddForex}
                icon="pi pi-plus"
                className="p-button-info"
                style={{ marginTop: "20px" }}
              />
            </div>
          </div>
        </div>
        <div className="card">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <ProgressSpinner
                style={{
                  display: userDetails.showLoader,
                  width: "40px",
                  height: "40px",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  bottom: "50%",
                  right: "50%",
                  zIndex: "1111111111",
                }}
                strokeWidth="3"
                animationDuration=".5s"
              />
            </div>
          ) : (
            <DataTable
              value={rates}
              paginator
              rows={5}
              rowsPerPageOptions={[5, 10, 25, 50]}
              tableStyle={{ minWidth: "60rem" }}
              className="p-datatable-responsive-demo"
            >
              <Column
                field="id"
                header="Id"
                style={{ width: "25%", fontSize: "13px" }}
              ></Column>
              <Column
                field="institutionName"
                header="Institution Name"
                style={{ width: "25%", fontSize: "13px" }}
              ></Column>
              <Column
                field="institutionId"
                header="Institution Id"
                style={{ width: "25%", fontSize: "13px" }}
              ></Column>
              <Column
                field="countryCode"
                header="Country Code"
                style={{ width: "25%", fontSize: "13px" }}
                body={countryBodyTemplate}
              ></Column>
              <Column
                field="tradingCurrency"
                header="Trading Currency"
                style={{ width: "25%", fontSize: "13px" }}
              ></Column>
              <Column
                field="customRate"
                header="Custom Rate"
                style={{ width: "25%", fontSize: "13px" }}
              ></Column>
              <Column
                field="createdAt"
                header="Created At"
                style={{ width: "25%", fontSize: "13px" }}
                body={dateTemplate}
              ></Column>
            </DataTable>
          )}
        </div>
      </section>
      <CustomDialog
        visible={showAddModal}
        header="Add Forex Modal"
        onClose={() => setShowAddModal(false)}
      >
        <div className="p-grid p-fluid" style={{ marginTop: "1em" }}>
          <div className="p-col-12 p-sm-12 p-md-12 p-lg-12 p-xl-12">
            <div className="p-grid p-fluid">
              <div className="p-col-12 p-mt-3">
                <span className="p-float-label">
                  <Dropdown
                    id="institutionName"
                    name="institutionName"
                    options={institutions.map((inst) => ({
                      label: inst.name,
                      value: inst.name,
                      user_id: inst.user_id,
                    }))}
                    value={formData.institutionName}
                    onChange={handleInstitutionChange}
                    optionLabel="label"
                    style={{ width: "100%" }}
                  />
                  <label style={labelStyle}>Institution Name</label>
                </span>
              </div>

              <div className="p-col-12 p-mt-3">
                <span className="p-float-label">
                  <Dropdown
                    id="countryCode"
                    name="countryCode"
                    options={countries.map((data) => ({
                      label: data.shortName,
                      value: data.shortName,
                    }))}
                    value={formData.countryCode}
                    onChange={handleChange}
                    optionLabel="label"
                    style={{ width: "100%" }}
                  />
                  <label style={labelStyle}>Country Code</label>
                </span>
              </div>

              <div className="p-col-12 p-mt-3">
                <span className="p-float-label">
                  <Dropdown
                    id="tradingCurrency"
                    name="tradingCurrency"
                    options={countries.map((data) => ({
                      label: data.currency,
                      value: data.currency,
                    }))}
                    value={formData.tradingCurrency}
                    onChange={handleChange}
                    optionLabel="label"
                    style={{ width: "100%" }}
                  />
                  <label style={labelStyle}>Trading Currency</label>
                </span>
              </div>

              <div className="p-col-12 p-mt-3">
                <span className="p-float-label">
                  <InputText
                    name="customRate"
                    type="number"
                    onChange={handleChange}
                    style={{ width: "100%" }}
                  />
                  <label style={labelStyle}>Custom Rate</label>
                </span>
              </div>

              <div
                className="p-col-12 p-md-12 p-lg-12 p-xl-12"
                style={{ textAlign: "center" }}
              >
                <Button
                  label="Submit"
                  className="p-button-info p-mx-auto"
                  onClick={handleSubmitForex}
                  style={{ width: "50%" }}
                />
              </div>
            </div>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default Forex;
